<template>
    <v-dialog v-model="open" :fullscreen="$vuetify.breakpoint.xs" :max-width="$vuetify.breakpoint.lgAndUp ? 1225 : 682" @click:outside="close" >
        <v-card :disabled="isConsultation">
            <v-container fluid>
                <MessageOverlay ref="overlay" />
                <v-row justify="center" justify-lg="space-between">
                    <v-col cols="auto">
                        <v-skeleton-loader
                            v-if="loading"
                            :width="webImageWidth"
                            :height="webImageHeight"
                            type="image"
                        ></v-skeleton-loader>
                        <v-hover>
                            <template v-slot:default="{ hover }">
                                <div class="imageContainer">
                                    <canvas
                                        id="canvasOutput"
                                        :width="webImageWidth"
                                        :height="webImageHeight"
                                        style="background-color:black"
                                        :style="loading ? 'display: none' : hover ? 'cursor: crosshair' : ''"
                                        @click="drawOnImage"
                                    ></canvas>
                                </div>
                            </template>
                        </v-hover>
                        <img id="canvasInput" style="display: none" :width="utcImageWidth" :height="utcImageHeight" @load="loadOutput">
                    </v-col>
                    <v-col cols="12" lg="" :style="{ maxWidth: (webImageWidth + 2*12) + 'px' }" class="d-flex flex-column justify-space-between">
                        <v-data-table
                            :headers="headers"
                            :items="masksData"
                            hide-default-footer
                            class="elevation-1"
                            :style="{ maxWidth: webImageWidth + 'px' }"
                            mobile-breakpoint="0"
                        >
                            <template v-slot:[`item.name`]="{ item }">
                                <div>M{{ item.name }}</div>
                            </template>
                            <template v-slot:[`item.restrictAlarm`]="{ item }">
                                <template>
                                    <v-row justify="center">              
                                        <v-checkbox v-model="item.restrictAlarm" @change="loadOutput"></v-checkbox>
                                        <v-icon small class="mr-2" color="black">
                                            mdi-alarm-light-off-outline
                                        </v-icon>
                                    </v-row>
                                </template>
                            </template>
                            <template v-slot:[`item.actions`]="{ item }">
                                <v-icon small @click="deleteMask(item)">mdi-delete</v-icon>
                            </template>
                        </v-data-table>
                        <v-alert :style="{ maxWidth: webImageWidth + 'px' }" class="text-body-2 mb-2 mt-5 mt-lg-0" color="warning" icon="mdi-alert" dense outlined>
                            {{ $vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.alertdelete') }}
                            <br />
                            {{ $vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.pleaseconsider') }}
                        </v-alert>
                    </v-col>
                </v-row>
                <v-row justify="center" align="center" class="mt-0">
                    <v-col cols="auto">
                        <v-alert :width="webImageWidth" class="gradient-wintech text-body-2 mb-0" icon="$info" dense outlined >
                            {{ $vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.createmaskinfo') }}
                            {{ $vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.maxsize') }}
                        </v-alert>
                    </v-col>
                    <v-col :style="{ maxWidth: (webImageWidth + 2*12) + 'px' }">
                        <v-card-actions class="px-0">
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        v-bind="attrs"
                                        v-on="on"
                                        fab
                                        :small="$vuetify.breakpoint.xs"
                                        class="gradient-wintech mr-2"
                                        @click="takeScreenshot"
                                    >
                                        <v-icon>mdi-camera-wireless</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ $vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.takeapictureremotely') }}</span>
                            </v-tooltip>
                            <v-spacer />
                            <v-btn
                                :class="!hasDataChanged ? '' : 'gradient-wintech'"
                                @click="sendToUTC"
                                :disabled="!hasDataChanged"
                                :small="$vuetify.breakpoint.xs"
                            >
                                <v-icon left>mdi-send-outline</v-icon>
                                {{ $vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.sendtoutc') }}
                            </v-btn>
                            <v-btn
                                color="red"
                                @click="close"
                                :small="$vuetify.breakpoint.xs"
                            >
                                <v-icon left>mdi-cancel</v-icon>
                                {{ $vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.cancel') }}
                            </v-btn>
                        </v-card-actions>
                    </v-col>
                </v-row>
            </v-container>
        </v-card>                     
    </v-dialog>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import cv from '@/plugins/opencv';
import MessageOverlay from '@/components/Overlay/MessageOverlay.vue'
import _ from 'lodash';
var cloneDeep = require('lodash.clonedeep');

export default {
    name: "MaskDetail",
    components: {
        MessageOverlay,
    },
    props: {
        open: Boolean,
        serialnb: String,
        lastScreenshot: Object,
        masks: Array,
        regions: Array,
        points: Array,
        regionRules: Array,
        pointRules: Array,
    },
    data() {
        return {
            loading: true,
            
            utcImageWidth: 170,
            utcImageHeight: 120,
            utcColormapWidth: 10,
            
            topLeftBottomRight: [],

            masksData: cloneDeep(this.masks)
        }
    },
    
    computed  : {
        ...mapGetters({ user: "StateUser" }),
        isConsultation() {
            return this.user.role === 'CONSULTATION'
        },
        isTiff() {
            return this.lastScreenshot.tiff !== undefined
        },
        webImageWidth() {
            return Math.min(this.$vuetify.breakpoint.width - (2 * 12) - (this.$vuetify.breakpoint.smAndUp ? (2 * 24) : 0), 650)
        },
        webImageHeight() {
            return this.webImageWidth * 460 / 650
        },
        webColormapWidth() {
            return this.utcColormapWidth * this.webImageWidth / this.utcImageWidth
        },
        hasDataChanged() {
            return !_.isEqual(this.masks, this.masksData)
        },
        headers() {
            return [  
                {
                    text: this.$vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.mask') + " (" + this.masksData.length + "/5)",
                    value: 'name',
                    class: 'table-header-text',
                    sortable: false,
                    align: 'center',
                    filterable: false,
                    width: "30%"
                },
                {
                    text: this.$vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.restrictAlarm'),
                    value: 'restrictAlarm',
                    class: 'table-header-text',
                    sortable: false,
                    align: 'center',
                    filterable: false,
                    width: "40%"
                },
                {
                    text: this.$vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.actions'),
                    value: 'actions',
                    class: 'table-header-text',
                    sortable: false,
                    align: 'center',
                    filterable: false,
                    width: "30%"
                },
            ]
        }
    },

    watch: {
        lastScreenshot: {
            async handler(newScreenshot) {
                this.loading = true;
                setTimeout(() => {
                    let input = document.getElementById('canvasInput');
                    if (newScreenshot.tiff !== undefined) {
                        this.loadOutput();
                    } else {
                        input.src = newScreenshot.url;
                    }
                    this.loading = false;
                }, 1000)
            },
            flush: 'post'
        },
        open (newOpen) {
            newOpen && setTimeout(() => {
                let input = document.getElementById('canvasInput');
                if (this.isTiff) {
                    this.loadOutput();
                } else {
                    input.src = this.lastScreenshot.url;
                }
                this.loading = false;
            }, 1000)
        }
    },

    methods: {
        close() {
            this.topLeftBottomRight = [];
            this.$emit('close');
        },

        async takeScreenshot() {
            try {
                const res = await axios.post('utcs/'+ this.serialnb +'/takeScreenshot');
                if (res.status == 200) {
                    this.$refs.overlay.showOverlay("success", this.$vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.screenshotOrdersentOK'));
                }
            } catch(err) {
                this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.screenshotOrdersentERR'));
            }
        },

        deleteMask(mask) {
            if (this.masksData.includes(mask)) this.masksData.splice(this.masksData.indexOf(mask), 1);
            this.loadOutput();
        },

        xToUTCScale(x) {
            return Math.round(x*(this.utcImageWidth/this.webImageWidth))
        },

        yToUTCScale(y) {
            return Math.round(y*(this.utcImageHeight/this.webImageHeight))
        },

        xToWebScale(x) {
            return Math.round(x*(this.webImageWidth - this.webColormapWidth)/160)
        },

        yToWebScale(y) {
            return Math.round(y*(this.webImageHeight/this.utcImageHeight))
        },
        
        drawRectangle(nature, mat, rectangle, color, type) {
            let tl = new cv.Point(this.xToWebScale(rectangle.x_tl), this.xToWebScale(rectangle.y_tl));
            let br = new cv.Point(this.xToWebScale(rectangle.x_tl + rectangle.width), this.xToWebScale(rectangle.y_tl + rectangle.height));
            const text = nature + rectangle.name;
            cv.rectangle(mat, tl , br, color, type);
            cv.putText(mat, text, { x: this.xToWebScale(rectangle.x_tl), y: this.xToWebScale(rectangle.y_tl) -3 }, cv.FONT_HERSHEY_DUPLEX, 0.5, color);
        },

        addNewMask(point) {
            if (this.masksData.length < 5) {
                this.topLeftBottomRight.push(point);
                if (this.topLeftBottomRight.length%2 === 0) {
                    let newMask = {};
                    if (this.topLeftBottomRight[0].x<this.topLeftBottomRight[1].x && this.topLeftBottomRight[0].y<this.topLeftBottomRight[1].y) {
                        newMask.x_tl = this.xToUTCScale(this.topLeftBottomRight[0].x);
                        newMask.y_tl = this.yToUTCScale(this.topLeftBottomRight[0].y);
                    }
                    else if (this.topLeftBottomRight[0].x>this.topLeftBottomRight[1].x && this.topLeftBottomRight[0].y>this.topLeftBottomRight[1].y) {
                        newMask.x_tl = this.xToUTCScale(this.topLeftBottomRight[1].x);
                        newMask.y_tl = this.yToUTCScale(this.topLeftBottomRight[1].y);
                    }
                    else if (this.topLeftBottomRight[0].x<this.topLeftBottomRight[1].x && this.topLeftBottomRight[0].y>this.topLeftBottomRight[1].y) {
                        newMask.x_tl = this.xToUTCScale(this.topLeftBottomRight[0].x);
                        newMask.y_tl = this.yToUTCScale(this.topLeftBottomRight[1].y);
                    }
                    else if (this.topLeftBottomRight[0].x>this.topLeftBottomRight[1].x && this.topLeftBottomRight[0].y<this.topLeftBottomRight[1].y) {
                        newMask.x_tl = this.xToUTCScale(this.topLeftBottomRight[1].x);
                        newMask.y_tl = this.yToUTCScale(this.topLeftBottomRight[0].y);
                    }
                    let width = Math.abs(this.xToUTCScale(this.topLeftBottomRight[1].x) - this.xToUTCScale(this.topLeftBottomRight[0].x))
                    let height = Math.abs(this.yToUTCScale(this.topLeftBottomRight[1].y) - this.yToUTCScale(this.topLeftBottomRight[0].y))
                    if (width*height <= (this.utcImageWidth - this.utcColormapWidth)*this.utcImageHeight/10) {
                        newMask.width = width;
                        newMask.height = height;
                        let usedNumbers = this.masksData.map(mask => parseInt(mask.name));
                        let smallestUnusedNumber = 1;
                        while (usedNumbers.includes(smallestUnusedNumber)) {
                            smallestUnusedNumber++;
                        }
                        newMask.name = "" + smallestUnusedNumber;
                        newMask.serialnb = this.serialnb;
                        newMask.restrictAlarm = 0;
                        this.masksData.push(newMask);
                    } else {  
                        this.$refs.overlay.showOverlay("success", this.$vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.tenthofimage'))
                    }
                    this.topLeftBottomRight = [];
                }
            } else {
                this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.youwillexceedthenumberofauthorizedareas'))
            }
        },


        drawElements(outputMat) {
            for (const mask of this.masksData) {
                this.drawRectangle("M", outputMat, mask, new cv.Scalar(255, 0, 0, 255), 2);
                if (+mask.restrictAlarm === 1) {
                    let lineColor = new cv.Scalar(255, 0, 0, 255); 
                    let tl = new cv.Point(this.xToWebScale(mask.x_tl), this.yToWebScale(mask.y_tl));
                    let tr = new cv.Point(this.xToWebScale(mask.x_tl + mask.width), this.yToWebScale(mask.y_tl));
                    let br = new cv.Point(this.xToWebScale(mask.x_tl + mask.width), this.yToWebScale(mask.y_tl + mask.height));
                    let bl = new cv.Point(this.xToWebScale(mask.x_tl), this.yToWebScale(mask.y_tl + mask.height));
                    cv.line(outputMat, tl, br, lineColor, 2, cv.LINE_AA);
                    cv.line(outputMat, bl, tr, lineColor, 2, cv.LINE_AA);
                }
            }
        },

        loadOutput() {
            if (this.lastScreenshot.url !== undefined) {
                var src;
                if (this.isTiff) {
                    src = cv.matFromArray(this.utcImageHeight, this.utcImageWidth, cv.CV_8UC3, this.lastScreenshot.tiff[0].data);
                } else {
                    let input = document.getElementById('canvasInput');
                    src = cv.imread(input);
                }
                let dsize = new cv.Size(this.webImageWidth, this.webImageHeight);
                let mat = new cv.Mat.zeros(src.rows, src.cols, cv.CV_8UC3);
                cv.resize(src, mat, dsize, 0, 0, cv.INTER_AREA);
                this.drawElements(mat);
                let output = document.getElementById('canvasOutput');
                cv.imshow(output, mat);
                src.delete(); mat.delete();
            }
        },

        drawOnImage(event) {
            if (this.lastScreenshot.url !== undefined) {
                var src; 
                if (this.isTiff) {
                    src = cv.matFromArray(this.utcImageHeight, this.utcImageWidth, cv.CV_8UC3, this.lastScreenshot.tiff[0].data);
                } else {
                    let input = document.getElementById('canvasInput');
                    src = cv.imread(input);
                }
                let dsize = new cv.Size(this.webImageWidth, this.webImageHeight);
                let mat = new cv.Mat.zeros(src.rows, src.cols, cv.CV_8UC3);
                cv.resize(src, mat, dsize, 0, 0, cv.INTER_AREA);
                if (mat.isContinuous() && event.offsetX <= 160*mat.cols/this.utcImageWidth) {
                    let point = new cv.Point(event.offsetX, event.offsetY)
                    this.addNewMask(point);
                }
                this.drawElements(mat);
                let output = document.getElementById('canvasOutput');
                cv.imshow(output, mat);
                src.delete(); mat.delete();
            }
        },

        async sendToUTC() {  
            let dataRPM = "";

            dataRPM += "\"P\":\"";
            for (const point of this.points) {
                dataRPM += point.name + "|";
                dataRPM += Math.round(point.x) + "|" + Math.round(point.y) + "|n"
            }

            dataRPM += "\",\"R\":\"";
            for (const region of this.regions) {
                dataRPM += region.name + "|";
                dataRPM += Math.round(region.x_tl) + "|" + Math.round(region.y_tl) + "|" + Math.round(region.width) + "|" + Math.round(region.height) + "|n";
            }

            dataRPM += "\",\"M\":\"";
            for (const mask of this.masksData) {
                dataRPM += mask.name + "|";
                dataRPM += Math.round(mask.x_tl)+"|"+Math.round(mask.y_tl)+"|"+Math.round(mask.width)+"|"+Math.round(mask.height)+"|"+(+mask.restrictAlarm)+"|n";
            }

            dataRPM += "\",\"RU\":\"";
            let allRules = this.regionRules.concat(this.pointRules);
            for (const rule of allRules) {
                dataRPM += rule.type + "|" + (typeof(rule.items) === "string" ? rule.items.replace(/R|P/g, "") : rule.items.join(";").replace(/R|P/g, "")) + "|" + rule.value + "|n";
            }
            dataRPM += "\"";

            try {
                const res = await axios.post('areapoint/rpm', {
                    serialnb: this.serialnb,
                    data: dataRPM
                });
                if (res.status == 200) {
                    this.$refs.overlay.showOverlay("success", this.$vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.yourorderhasbeensenttoUTC'));
                    this.$emit("update:masks", cloneDeep(this.masksData));
                }
            } catch(err) {
                console.error(err)
                this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.utc.controlsCard.maskDetail.yourordercouldnotbesenttoUTC'))
            }

            this.reset();
        },

        reset() {
            this.topLeftBottomRight = [];
            this.loadOutput();
        },
    }
}
</script>